import React, { useEffect, useState } from 'react'
import { Bell, BellOff, Download, Share } from 'lucide-react'
import { pushAPI } from '../services/api'
import { getInstallPrompt, promptInstall, isStandalone, isIOS } from '../pwaInstall'

/**
 * PWA-paneel in de dashboard-sidebar (2026-09-10):
 * - "App installeren" (Android/Chrome via beforeinstallprompt)
 * - iPhone-hint "Deel → Zet op beginscherm" (Safari heeft geen install-prompt)
 * - Pushmeldingen aan/uit (Web Push via service worker + VAPID)
 * Wit met border, geen kleurvlakken (huisstijl).
 */
const DISMISS_KEY = 'ugc4you_pwa_hint_dismissed'

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  return Uint8Array.from(Array.from(raw).map((c) => c.charCodeAt(0)))
}

export default function PwaPanel() {
  const supported = typeof window !== 'undefined' && 'serviceWorker' in navigator
  const pushSupported = supported && 'PushManager' in window && 'Notification' in window
  const [installEvent, setInstallEvent] = useState(() => (typeof window !== 'undefined' ? getInstallPrompt() : null))
  const [installed, setInstalled] = useState(() => (typeof window !== 'undefined' ? isStandalone() : false))
  const [hintDismissed, setHintDismissed] = useState(() => { try { return localStorage.getItem(DISMISS_KEY) === '1' } catch { return false } })
  const [pushState, setPushState] = useState('unknown') // unsupported | denied | off | on | busy

  useEffect(() => {
    if (!supported) return undefined
    const onPrompt = () => setInstallEvent(getInstallPrompt())
    const onInstalled = () => { setInstalled(true); setInstallEvent(null) }
    window.addEventListener('ugc-installable', onPrompt)
    window.addEventListener('ugc-installed', onInstalled)
    return () => { window.removeEventListener('ugc-installable', onPrompt); window.removeEventListener('ugc-installed', onInstalled) }
  }, [supported])

  useEffect(() => {
    if (!pushSupported) { setPushState('unsupported'); return undefined }
    if (Notification.permission === 'denied') { setPushState('denied'); return undefined }
    let cancelled = false
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => { if (!cancelled) setPushState(sub ? 'on' : 'off') })
      .catch(() => { if (!cancelled) setPushState('off') })
    return () => { cancelled = true }
  }, [pushSupported])

  const install = async () => {
    const outcome = await promptInstall()
    if (outcome === 'accepted') setInstalled(true)
    setInstallEvent(getInstallPrompt())
  }

  const enablePush = async () => {
    try {
      setPushState('busy')
      const perm = await Notification.requestPermission()
      if (perm !== 'granted') { setPushState(perm === 'denied' ? 'denied' : 'off'); return }
      const { data } = await pushAPI.publicKey()
      if (!data || !data.publicKey) { setPushState('off'); return }
      const reg = await navigator.serviceWorker.ready
      let sub = await reg.pushManager.getSubscription()
      if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(data.publicKey) })
      await pushAPI.subscribe(sub.toJSON())
      setPushState('on')
      pushAPI.test().catch(() => {})
    } catch (err) {
      console.error('Pushmeldingen inschakelen mislukt:', err)
      setPushState('off')
    }
  }

  const disablePush = async () => {
    try {
      setPushState('busy')
      const reg = await navigator.serviceWorker.ready
      const sub = await reg.pushManager.getSubscription()
      if (sub) { await pushAPI.unsubscribe(sub.endpoint).catch(() => {}); await sub.unsubscribe() }
    } catch { /* genegeerd */ }
    setPushState('off')
  }

  const dismissHint = () => { setHintDismissed(true); try { localStorage.setItem(DISMISS_KEY, '1') } catch { /* genegeerd */ } }

  if (!supported) return null
  const showInstall = !installed && !!installEvent
  const showIosHint = !installed && isIOS() && !installEvent && !hintDismissed
  const showPush = pushState !== 'unsupported' && pushState !== 'unknown' && !(isIOS() && !installed)
  if (!showInstall && !showIosHint && !showPush) return null

  const rowClass = 'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 transition min-h-[44px] text-left'

  return (
    <div className="mb-2 rounded-xl border border-[#E5E7EB] bg-white p-1.5 text-sm" data-tour="pwa-panel">
      {showInstall && (
        <button type="button" onClick={install} className={`${rowClass} text-[#0A0F0D] font-semibold`}>
          <Download className="w-4 h-4 text-[#3EB489] flex-shrink-0" />
          <span>App installeren</span>
        </button>
      )}
      {showIosHint && (
        <div className="px-3 py-2 text-[#475569] leading-snug">
          <div className="flex items-start gap-2">
            <Share className="w-4 h-4 mt-0.5 text-[#3EB489] flex-shrink-0" />
            <span>Als app op je iPhone: tik op <strong>Deel</strong> en kies <strong>Zet op beginscherm</strong>.</span>
          </div>
          <button type="button" onClick={dismissHint} className="mt-1 text-xs text-[#94A3B8] hover:text-[#0A0F0D] min-h-[32px]">Niet meer tonen</button>
        </div>
      )}
      {showPush && (
        pushState === 'on' ? (
          <button type="button" onClick={disablePush} className={`${rowClass} text-[#475569]`}>
            <Bell className="w-4 h-4 text-[#3EB489] flex-shrink-0" />
            <span>Meldingen staan aan</span>
            <span className="ml-auto text-xs text-[#94A3B8]">uitzetten</span>
          </button>
        ) : pushState === 'denied' ? (
          <div className="px-3 py-2 text-xs text-[#94A3B8] flex items-center gap-2">
            <BellOff className="w-4 h-4 flex-shrink-0" />
            <span>Meldingen zijn geblokkeerd in je browserinstellingen</span>
          </div>
        ) : (
          <button type="button" onClick={enablePush} disabled={pushState === 'busy'} className={`${rowClass} text-[#0A0F0D] font-semibold disabled:opacity-60`}>
            <Bell className="w-4 h-4 text-[#3EB489] flex-shrink-0" />
            <span>{pushState === 'busy' ? 'Bezig...' : 'Meldingen aanzetten'}</span>
          </button>
        )
      )}
    </div>
  )
}
